import { Languages } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';
import { LANGUAGES, LANGUAGE_LABELS, LANGUAGE_SHORT_LABELS } from '../../constants/language';

interface LanguageToggleProps {
  /**
   * `segmented` shows both languages side by side, for places with room to spare.
   * `compact` is a single button showing the language you would switch *to* —
   * for headers and toolbars.
   */
  variant?: 'segmented' | 'compact';
  className?: string;
}

/**
 * Switches the interface language.
 *
 * Both language names are always written in their own script and never translated:
 * someone stranded in a language they cannot read has to be able to find their way
 * back out, which a translated label would prevent.
 */
export function LanguageToggle({ variant = 'compact', className }: Readonly<LanguageToggleProps>) {
  const { language, setLanguage, toggleLanguage, t } = useLanguage();

  if (variant === 'segmented') {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5',
          className
        )}
      >
        {LANGUAGES.map((code) => {
          const isActive = code === language;
          return (
            <button
              key={code}
              type="button"
              onClick={() => setLanguage(code)}
              aria-pressed={isActive}
              className={cn(
                'rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors duration-150',
                isActive
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              )}
            >
              {LANGUAGE_LABELS[code]}
            </button>
          );
        })}
      </div>
    );
  }

  const nextLanguage = language === 'en' ? 'bn' : 'en';

  return (
    <button
      type="button"
      onClick={toggleLanguage}
      title={t('language.switchTo', { language: LANGUAGE_LABELS[nextLanguage] })}
      aria-label={t('language.switchTo', { language: LANGUAGE_LABELS[nextLanguage] })}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs font-semibold text-slate-400 transition-all duration-150 hover:border-violet-500/40 hover:bg-violet-500/10 hover:text-violet-300',
        className
      )}
    >
      <Languages className="h-3.5 w-3.5 flex-shrink-0" />
      {LANGUAGE_SHORT_LABELS[nextLanguage]}
    </button>
  );
}
